import React, { useState } from 'react';
import './AddButton.css';

// + icon as SVG (not literal "+" text) so the 45° hover rotation reads
// cleanly. strokeWidth tuned to match the Metropolis weight of the label.
const PlusIcon = () => (
  <svg
    className="add-button-icon add-button-icon--plus w-4 h-4"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.25"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M8 3v10M3 8h10" />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="add-button-icon add-button-icon--check w-4 h-4"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 8.5l3 3 7-7" />
  </svg>
);

const Spinner = () => (
  <svg
    className="add-button-icon w-4 h-4 animate-spin"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" />
    <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

// Variant tokens — codify the brief's three-tier button system
// (2026-06 design-system §2E):
//
//   primary    Fusion-Green fill + black text. The single positive-
//              outcome CTA per view (e.g. New Brief, Save).
//   secondary  Neutral grey fill. Sits beside primary without
//              competing — used for "+ New Supplier", "+ Add User"
//              etc., where the action is creative but secondary in
//              priority to the primary CTA.
//   tertiary   Transparent + 1px border + off-white label. Hover
//              brightens the border and lifts label toward active-
//              green. The legacy `outline` alias points here so
//              prior callers keep working byte-identically.
//
// Hover brightness on `primary` is handled in CSS (filter:brightness)
// so it doesn't need its own hover-bg override — that would compete
// with the brightness shift.
// 2026-06 annotation: secondary differs more from tiles. The old
// #2b2e28 fill sat too close to the page card tone (#181A16) — barely
// readable as a button against an adjacent tile. Lifted to #3a3c34
// (notably brighter than card bg) for clearer separation. Hover lifts
// further to #4a4d44 so the affordance still has somewhere to go.
// Fusion shared button tiers, on the LIGHT content area (A1/A4):
//   primary    Shared primary button (A4) — lime fill, black text,
//              radius 10, weight 800, subtle lime shadow. The single
//              positive-outcome CTA per view.
//   secondary  White fill + forest label + line border. Sits beside
//              primary without competing ("+ New Supplier" etc.).
//   tertiary / outline  Transparent + line border + forest label.
// No lime text on light (A1) — labels are forest #004733.
// App-UI button hierarchy (§7 + build brief #8 Task C): one primary per
// view (solid green, dark #14210A label). AddButton always denotes a
// create/add action (it renders a +), so its secondary tier is the
// "secondary create" — white fill + deep-green #009159 outline, label
// and + in the same deep green so it reads as a create action without
// competing with the solid-green primary. Neutral utility buttons
// (Widgets etc.) are plain <button>s with a grey border, not AddButton.
// Height 40px, radius 10, 0 18px padding, 600 weight.
const VARIANT_CLASSES = {
  primary:   'rounded-[10px] font-semibold bg-fusion-green hover:bg-fusion-green-bright text-[#14210A] shadow-[0_8px_18px_-8px_rgba(146,212,0,0.7)]',
  secondary: 'rounded-[10px] font-semibold bg-white text-fusion-emerald border border-fusion-emerald hover:bg-fusion-emerald/[0.06]',
  tertiary:  'rounded-[10px] font-semibold bg-transparent text-ink-secondary hover:bg-black/[0.04]',
  // Legacy alias — earlier code used `outline` before the tiers were
  // named; map it to secondary so every caller stays valid.
  outline:   'rounded-[10px] font-semibold bg-white text-fusion-emerald border border-fusion-emerald hover:bg-fusion-emerald/[0.06]',
};

export default function AddButton({
  label,
  onClick,
  variant = 'outline',
  disabled = false,
  type = 'button',
  className = '',
  ariaLabel,
}) {
  // Three-phase state machine. We only enter pending/success when the
  // caller's onClick actually returns a thenable — synchronous handlers
  // (navigate(), setShowModal(true)) skip both phases entirely, which is
  // what the spec wants for modal-mediated actions.
  const [phase, setPhase] = useState('idle');

  const handleClick = async (e) => {
    if (disabled || phase !== 'idle') return;
    let result;
    try {
      result = onClick?.(e);
    } catch (err) {
      throw err;
    }
    if (result && typeof result.then === 'function') {
      setPhase('pending');
      try {
        await result;
        setPhase('success');
        setTimeout(() => setPhase('idle'), 600);
      } catch (err) {
        setPhase('idle');
        throw err;
      }
    }
  };

  const variantClasses = VARIANT_CLASSES[variant] || VARIANT_CLASSES.outline;
  const icon =
    phase === 'pending' ? <Spinner /> :
    phase === 'success' ? <CheckIcon /> :
    <PlusIcon />;

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled || phase === 'pending'}
      aria-label={ariaLabel}
      className={`add-button font-['Metropolis'] text-sm px-[18px] h-10 inline-flex items-center justify-center gap-2 ${variantClasses} ${className}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
